/**
 * Reglas de validación de los campos de comercio y sucursal.
 *
 * Antes vivían duplicadas en `models/Commerce.ts`, `models/Branch.ts` y
 * `hooks/useCommerceValidations.ts`, con límites distintos para el mismo campo
 * (el nombre se validaba a 20 en el schema y a 50 en el hook) y sin coincidir
 * con las columnas de la base ni con el dato real:
 *
 *   - Los nombres y razones sociales sólo aceptaban ASCII, así que rebotaban
 *     los acentos y la ñ, y cualquier signo fuera de `-_.,&()`. Nombres reales
 *     como `1+N PERU`, `Real Pet Perú` o `BILLETERAS&CORREAS` daban error.
 *   - `taxNumber` exigía exactamente 12 dígitos, que es el largo del RUT
 *     uruguayo. El RUC peruano tiene 11 y el DNI 8: 728 de los 733 comercios
 *     de Perú no pasaban.
 *   - `doorNumber` no aceptaba `/`, así que rebotaba `S/N`.
 *
 * Nombres y direcciones son texto libre escrito por gente. En vez de una lista
 * de caracteres permitidos, que se queda corta cada vez que aparece un caso
 * nuevo, se rechaza sólo lo que no puede haber: caracteres de control y los
 * signos de markup.
 *
 * Los largos son los de las columnas de la base. Espejo de
 * `business/src/core/dto/fieldRules.ts`; si cambia uno, cambia el otro.
 */

/** Texto libre: cualquier cosa menos caracteres de control y `<` `>`. */
// eslint-disable-next-line no-control-regex
export const FREE_TEXT_REGEX = /^[^\u0000-\u001F\u007F<>]+$/;
export const FREE_TEXT_MESSAGE = 'Contiene caracteres no permitidos';

/** commerces.name / branches.name — varchar(100) */
export const NAME_MIN = 3;
export const NAME_MAX = 100;

/** commerces.business_name / branches.business_name — varchar(100) */
export const BUSINESS_NAME_MAX = 100;

/** addresses.street_name — varchar(500) */
export const STREET_NAME_MAX = 500;

/** addresses.door_number — varchar(10) */
export const DOOR_NUMBER_MAX = 10;

/** addresses.apartment_number — varchar(50) */
export const APARTMENT_NUMBER_MAX = 50;

/** addresses.corner — varchar(200) */
export const CORNER_MAX = 200;

/** commerces.external_id / branches.external_id — varchar(50) */
export const EXTERNAL_ID_MAX = 50;

/**
 * Documento fiscal: sólo dígitos, sin puntos ni guiones.
 *
 * El rango cubre los tres formatos que conviven hoy: RUT uruguayo (12), RUC
 * peruano (11) y DNI peruano (8). El piso queda en 6 porque hay documentos
 * cargados sin los ceros a la izquierda.
 */
export const TAX_NUMBER_MIN = 6;
export const TAX_NUMBER_MAX = 12;
export const TAX_NUMBER_REGEX = /^\d{6,12}$/;
export const TAX_NUMBER_MESSAGE =
  'El documento fiscal debe tener entre 6 y 12 dígitos, sin puntos ni guiones';

// No se valida el dígito verificador ni se descartan los repetidos: hay
// comercios en producción cargados con `99999999999` de relleno, y bloquearlos
// impediría editarles cualquier otro campo.
